import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { startAuthentication } from '@simplewebauthn/browser';
import { announcementsCache } from '@/services/announcementsCache';
import { STRINGS } from '@/constants/strings';
import { ROUTES } from '@/constants/routes';
import { VALIDATION_RULES } from '@/constants/validationRules';

interface LoginFormData {
  username: string;
  password: string;
}

function Login() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [showResetPrompt, setShowResetPrompt] = useState(false);
  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [resetUsername, setResetUsername] = useState('');
  const [codeUsername, setCodeUsername] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [showCodeMode, setShowCodeMode] = useState(false);
  const [inviteCode, setInviteCode] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/auth/login', {
        username: formData.username.trim(),
        password: formData.password,
      });
      const { accessToken, refreshToken, user } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      window.dispatchEvent(new Event('user-updated'));
      announcementsCache.clear(); // Clear dismissed announcements cache on fresh login

      navigate(ROUTES.chat);
    } catch (err: any) {
      setError(err.response?.data?.error || STRINGS.login_failed_error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/invites/reset-password/request', {
        username: resetUsername,
      });
      setResetMessage(
        'If that username exists, a reset code was sent to the registered email address.',
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to request password reset');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < VALIDATION_RULES.password.min) {
      setError(`Password must be at least ${VALIDATION_RULES.password.min} characters`);
      return;
    }

    setLoading(true);

    try {
      await api.post('/invites/reset-password/confirm', {
        username: codeUsername,
        code: resetCode,
        newPassword,
      });

      setResetMessage('Password reset successful! You can now login with your new password.');
      setShowCodeEntry(false);
      setShowResetPrompt(false);
      setResetCode('');
      setCodeUsername('');
      setNewPassword('');
      setConfirmPassword('');
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.post('/invites/validate-code', {
        code: inviteCode,
      });

      const { type } = response.data;

      if (type === 'registration') {
        navigate(ROUTES.invite_register, { state: { code: inviteCode } });
      } else if (type === 'password_reset') {
        setShowCodeMode(false);
        setResetCode(inviteCode);
        setShowCodeEntry(true);
        setCodeUsername('');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyLogin = async () => {
    if (!formData.username) {
      setError('Please enter your username to use a passkey');
      return;
    }

    setError('');
    setPasskeyLoading(true);
    try {
      const { data: optionsRes } = await api.post('/passkeys/authenticate/options', {
        username: formData.username,
      });

      const assertion = await startAuthentication(optionsRes.options);

      const verifyRes = await api.post('/passkeys/authenticate/verify', {
        credential: assertion,
        challenge: optionsRes.challenge,
        userId: optionsRes.userId,
      });

      const { accessToken, refreshToken, user } = verifyRes.data;
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));
      window.dispatchEvent(new Event('user-updated'));
      navigate(ROUTES.chat);
    } catch (err: any) {
      console.error('Passkey login failed:', err);
      setError(err.response?.data?.error || 'Passkey login failed');
    } finally {
      setPasskeyLoading(false);
    }
  };

  if (showCodeMode) {
    return (
      <div className="min-h-screen bg-brand-background flex items-center justify-center p-4">
        <div className="glass-effect p-8 rounded-2xl shadow-2xl border border-brand-primary/30 w-full max-w-md">
          <h2 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-secondary mb-6 text-center">
            Enter Code
          </h2>

          {error && (
            <div className="bg-red-500/20 border border-red-500/50 text-red-200 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleCodeSubmit}>
            <div className="mb-6">
              <label className="block text-brand-on-background mb-2">6-Digit Code</label>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="w-full px-4 py-3 min-h-[44px] glass-effect border border-brand-primary/30 rounded-lg text-brand-on-background focus:outline-none focus:border-brand-primary text-center text-2xl tracking-widest font-mono"
                placeholder="000000"
              />
            </div>

            <button
              type="submit"
              disabled={loading || inviteCode.length !== 6}
              className="w-full py-3 bg-gradient-to-r from-brand-secondary to-brand-primary hover:opacity-90 text-brand-on-secondary rounded-lg font-semibold disabled:opacity-50"
            >
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>
          </form>

          <button
            onClick={() => {
              setShowCodeMode(false);
              setInviteCode('');
              setError('');
            }}
            className="mt-4 text-brand-on-background/60 hover:text-brand-on-background text-sm w-full text-center"
          >
            ← Back to Login
          </button>
        </div>
      </div>
    );
  }

  if (showCodeEntry) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-background">
        <div className="card max-w-md w-full">
          <h1 className="text-3xl font-bold text-center mb-6">Enter Reset Code</h1>
          <p className="text-sm text-brand-on-surface/60 text-center mb-4">
            Enter your username, the 6-digit code, and your new password below.
          </p>

          {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>}

          {resetMessage && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg text-sm">
              {resetMessage}
            </div>
          )}

          <form onSubmit={handleCodeReset} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Username</label>
              <input
                type="text"
                className="input min-h-[44px]"
                value={codeUsername}
                onChange={(e) => setCodeUsername(e.target.value)}
                placeholder="Your username"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">6-Digit Code</label>
              <input
                type="text"
                className="input min-h-[44px] text-center text-2xl tracking-widest"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">New Password</label>
              <input
                type="password"
                className="input min-h-[44px]"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                minLength={8}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Confirm New Password</label>
              <input
                type="password"
                className="input min-h-[44px]"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={loading || resetCode.length !== 6 || !codeUsername}
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowCodeEntry(false);
                setResetCode('');
                setCodeUsername('');
                setNewPassword('');
                setConfirmPassword('');
                setError('');
              }}
              className="btn btn-secondary w-full"
            >
              Back
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (showResetPrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-background">
        <div className="card max-w-md w-full">
          <h1 className="text-3xl font-bold text-center mb-6">Reset Password</h1>
          <p className="text-sm text-brand-on-surface/60 text-center mb-4">
            Enter the username and we will alert the admins to send you a new password.
          </p>

          {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">{error}</div>}

          {resetMessage && (
            <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg break-all text-sm">
              {resetMessage}
            </div>
          )}

          <form onSubmit={handleResetRequest} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-brand-on-surface">
                {STRINGS.username_label}
              </label>
              <input
                type="text"
                className="input min-h-[44px]"
                value={resetUsername}
                onChange={(e) => setResetUsername(e.target.value)}
                placeholder="Enter your username"
                required
              />
            </div>

            <button type="submit" className="btn btn-primary w-full" disabled={loading}>
              {loading ? 'Requesting...' : 'Request Reset Link'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowResetPrompt(false);
                setResetMessage('');
                setError('');
              }}
              className="btn btn-secondary w-full"
            >
              Back to Login
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-brand-background">
      <div className="card m3-dialog max-w-md w-full p-6 sm:p-8 rounded-3xl border border-brand-outline-variant/60 shadow-2xl bg-brand-surface/95 dark:bg-[#1f1c48]/95 backdrop-blur-xl">
        <div className="flex justify-center mb-6">
          <img
            src="/irus-logo.svg"
            alt="Irus"
            className="w-28 h-28 sm:w-36 sm:h-36 hover:scale-105 transition-transform"
          />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-center mb-6 text-brand-on-surface dark:text-white">
          {STRINGS.welcome_back}
        </h1>

        {error && <div className="mb-4 p-3.5 bg-red-500/20 text-red-300 border border-red-500/30 rounded-2xl text-sm">{error}</div>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="username" className="block text-xs font-semibold uppercase tracking-wider mb-1.5 text-brand-on-surface-variant dark:text-[#c4b5fd]">
              {STRINGS.username_label}
            </label>
            <input
              id="username"
              name="username"
              type="text"
              autoComplete="username"
              className="input rounded-xl min-h-[46px] border border-brand-outline-variant/80 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/30"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              required
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-semibold uppercase tracking-wider mb-1.5 text-brand-on-surface-variant dark:text-[#c4b5fd]">
              {STRINGS.password_label}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              className="input rounded-xl min-h-[46px] border border-brand-outline-variant/80 focus:border-brand-primary focus:ring-2 focus:ring-brand-primary/30"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              required
            />
          </div>

          <button type="submit" className="btn btn-primary w-full py-3 rounded-full text-base font-semibold shadow-md hover:shadow-lg active:scale-95 transition-all min-h-[48px]" disabled={loading}>
            {loading ? 'Logging in...' : STRINGS.sign_in_button}
          </button>

          <div className="pt-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex-1 h-px bg-brand-outline-variant/60"></div>
              <span className="text-xs text-brand-on-surface-variant/70 uppercase tracking-wider">or</span>
              <div className="flex-1 h-px bg-brand-outline-variant/60"></div>
            </div>
            <button
              type="button"
              onClick={handlePasskeyLogin}
              className="btn btn-tonal w-full py-2.5 rounded-full font-medium border border-brand-outline-variant hover:bg-white/10 active:scale-95 transition-all min-h-[44px]"
              disabled={passkeyLoading}
            >
              🔑 {passkeyLoading ? 'Waiting for passkey…' : 'Login with Passkey'}
            </button>
          </div>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => setShowResetPrompt(true)}
              className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
            >
              Forgot password?
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowCodeMode(true)}
            className="mt-3 text-brand-on-surface-variant/70 hover:text-brand-on-background text-xs sm:text-sm w-full text-center transition-colors"
          >
            Have an invite or reset code? Click here
          </button>
        </form>
      </div>
    </div>
  );
}

export default Login;
